import BufferedEffects from './BufferedEffects.js';

/** Native Web Audio mixer. Public transport positions are milliseconds. */
export default class NativeMixer {
  constructor(context, count, emit, input) {
    this.context = context;
    this.emit = emit;
    this.input = input;
    this.master = context.createGain();
    this.master.connect(context.destination);
    this.masterAnalyser = this.makeAnalyser(this.master);
    this.generation = 0;
    this.reset(count);
  }

  makeAnalyser(node) {
    const analyser = this.context.createAnalyser();
    analyser.fftSize = 256;
    analyser.samples = new Float32Array(analyser.fftSize);
    node.connect(analyser);
    return analyser;
  }

  makeChannel(id) {
    const gain = this.context.createGain();
    const pan = this.context.createGain();
    pan.channelCount = 2;
    pan.channelCountMode = 'explicit';
    pan.channelInterpretation = 'speakers';
    const splitter = this.context.createChannelSplitter(2);
    const left = this.context.createGain();
    const right = this.context.createGain();
    const merger = this.context.createChannelMerger(2);
    // Preserve the old stereo-balance law: centre leaves both channels at unity.
    // Explicit speaker upmix duplicates mono to both outputs without attenuation.
    pan.connect(splitter);
    splitter.connect(left, 0); splitter.connect(right, 1);
    left.connect(merger, 0, 0); right.connect(merger, 0, 1);
    merger.connect(gain); gain.connect(this.master);
    return { id, gain, pan, splitter, left, right, merger,
      analyser: this.makeAnalyser(gain), level: 1, balance: 0.5, muted: false };
  }

  reset(count) {
    this.stop();
    this.loadGeneration = (this.loadGeneration || 0) + 1;
    if (this.recorder) this.recorder.port.postMessage({ type: 'cancel' });
    this.recordingTargets = null;
    (this.channels || []).forEach(channel => this.disconnectChannel(channel));
    this.channels = Array.from({ length: count }, (_, index) => this.makeChannel(index));
    this.duration = 0;
    this.speed = 1;
    this.pitchCents = 0;
    this.loop = null;
    this.master.gain.value = 1;
  }

  disconnectChannel(channel) {
    if (channel.effectNode) {
      if (this.effectDriver) this.effectDriver.disconnect(channel.effectNode);
      channel.effectNode.port.postMessage({ type: 'dispose' });
      channel.effectNode.port.close();
      channel.effectNode.disconnect();
      channel.effectNode = null;
    }
    for (const key of ['gain', 'pan', 'splitter', 'left', 'right', 'merger', 'analyser']) {
      channel[key].disconnect();
    }
    channel.buffer = null;
    channel.padded = null;
  }

  stopSources() {
    if (this.effectsSession) { this.effectsSession.cancel(); this.effectsSession = null; }
    (this.channels || []).forEach(channel => {
      if (channel.source) {
        channel.source.stop();
        channel.source.disconnect();
        if (channel.source === channel.effectNode) {
          if (this.effectDriver) this.effectDriver.disconnect(channel.effectNode);
          channel.effectNode.port.postMessage({ type: 'dispose' });
          channel.effectNode.port.close();
          channel.effectNode = null;
        }
        channel.source = null;
      }
    });
  }

  get position() {
    let position = this.offset || 0;
    if (this.playing) position += Math.max(0, this.context.currentTime - this.startedAt) * this.speed;
    if (this.loop && position >= this.loop.end) {
      position = this.loop.start + (position - this.loop.end) % (this.loop.end - this.loop.start);
    }
    return Math.min(position, this.duration || 0);
  }

  async play(offline = false) {
    if (this.playing || this.startPending || !this.duration) return;
    const generation = ++this.generation;
    this.startPending = true;
    try {
      if (!offline && this.context.resume) await this.context.resume();
      if (this.usingEffects) await this.prepareEffects(offline);
      if (generation !== this.generation) return;
      if (this.offset >= this.duration) this.offset = 0;
      if (this.loop && (this.offset < this.loop.start || this.offset >= this.loop.end)) this.offset = this.loop.start;
      if (this.usingEffects && !offline) {
        const session = this.createBufferedEffects();
        this.effectsSession = session;
        const when = await session.start();
        if (generation !== this.generation) { session.cancel(); return; }
        this.startedAt = when; this.playing = true;
      } else this.schedule(offline ? this.context.currentTime : this.context.currentTime + 0.02);
    } catch (error) {
      if (generation !== this.generation) return;
      this.pause();
      throw error;
    } finally {
      if (generation === this.generation) this.startPending = false;
    }
  }

  schedule(when) {
    if (this.loop && (this.offset < this.loop.start || this.offset >= this.loop.end)) {
      this.offset = this.loop.start;
    }
    this.startedAt = when;
    this.playing = true;
    this.channels.forEach(channel => {
      if (!channel.buffer) return;
      if (this.usingEffects) {
        const source = this.createEffectNode(channel, when);
        source.connect(channel.pan);
        channel.source = source;
        return;
      }
      let buffer = channel.buffer;
      if (this.loop && buffer.duration < this.loop.end) {
        const frames = Math.ceil(this.duration * this.context.sampleRate);
        if (!channel.padded || channel.padded.length !== frames) {
          channel.padded = this.context.createBuffer(buffer.numberOfChannels, frames, buffer.sampleRate);
          for (let c = 0; c < buffer.numberOfChannels; c++) {
            channel.padded.copyToChannel(buffer.getChannelData(c), c);
          }
        }
        buffer = channel.padded;
      }
      if (this.offset >= buffer.duration) return;
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      if (this.loop) {
        source.loop = true;
        source.loopStart = this.loop.start;
        source.loopEnd = this.loop.end;
      }
      source.connect(channel.pan);
      source.start(when, this.offset);
      channel.source = source;
    });
  }

  pause() {
    this.offset = this.position;
    this.playing = false;
    this.resumeAfterEffects = false;
    this.startPending = false;
    ++this.generation;
    this.stopSources();
  }

  stop() {
    this.pause();
    this.offset = 0;
  }

  seek(ms) {
    const playing = this.playing || this.startPending || this.resumeAfterEffects;
    this.pause();
    this.offset = Math.max(0, Math.min(this.duration, Number(ms) / 1000 || 0));
    const restart = playing ? this.play() : undefined;
    this.sendState();
    return restart;
  }

  setLoop(startMs, endMs) {
    const playing = this.playing || this.startPending || this.resumeAfterEffects;
    this.pause();
    const start = Math.max(0, Number(startMs) / 1000);
    const end = Math.min(this.duration, Number(endMs) / 1000);
    this.loop = Number.isFinite(start) && Number.isFinite(end) && end > start
      ? { start, end } : null;
    if (playing) return this.play();
  }

  get usingEffects() { return this.speed !== 1 || this.pitchCents !== 0; }

  createBufferedEffects() { return new BufferedEffects(this); }

  async prepareEffects(offline = typeof this.context.startRendering === 'function') {
    if (!offline) {
      if (typeof Worker === 'undefined') throw new Error('Pitch/speed processing needs browser Worker support.');
      return;
    }
    if (!this.context.audioWorklet) throw new Error('Independent pitch/speed requires AudioWorklet support (HTTPS or localhost).');
    if (!this.effectModulePromise) {
      this.effectModulePromise = this.context.audioWorklet.addModule(
        `${process.env.PUBLIC_URL || ''}/processorScripts/pitch-tempo-processor.js`
      ).catch(error => { this.effectModulePromise = null; throw error; });
    }
    const loadGeneration = this.loadGeneration;
    await this.effectModulePromise;
    if (loadGeneration !== this.loadGeneration) throw new Error('Effect preparation was cancelled');
    // A silent native source keeps worklet processing active through split/merge
    // routing on WebKit, including its OfflineAudioContext implementation.
    if (!this.effectDriver) {
      this.effectDriver = this.context.createConstantSource();
      this.effectDriver.offset.value = 0;
      this.effectDriver.start();
    }
  }

  createEffectNode(channel, when) {
    const channels = Array.from({ length: channel.buffer.numberOfChannels },
      (_, index) => channel.buffer.getChannelData(index));
    // Configure in processorOptions: offline rendering may run before a port
    // message sent just before startRendering has reached the processor.
    const node = new AudioWorkletNode(this.context, 'pitch-tempo-processor', {
      numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2],
      processorOptions: { channels, startFrame: Math.round(when * this.context.sampleRate),
        offset: this.offset, speed: this.speed, pitch: Math.pow(2, this.pitchCents / 1200),
        loop: this.loop, duration: this.duration }
    });
    node.stop = () => node.port.postMessage({ type: 'stop' });
    node.onprocessorerror = () => this.emit({ event: 'error', message: 'The pitch/speed processor failed. Stop playback and reload the song.' });
    this.effectDriver.connect(node);
    channel.effectNode = node;
    return node;
  }

  async setEffects(name, value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    if (name !== 'playbackRate' && name !== 'pitchShiftCents') return;
    const speed = name === 'playbackRate' ? Math.max(.5, Math.min(1.5, numeric)) : this.speed;
    const pitch = name === 'pitchShiftCents' ? Math.max(-1200, Math.min(1200, numeric)) : this.pitchCents;
    if (speed === this.speed && pitch === this.pitchCents) return;
    if (this.recordingTargets) throw new Error('Stop recording before changing pitch or speed.');
    const playing = this.playing || this.startPending || this.resumeAfterEffects;
    this.pause(); // Capture the position using the old tempo before changing it.
    this.speed = speed;
    this.pitchCents = pitch;
    this.resumeAfterEffects = playing;
    const generation = this.generation;
    if (this.usingEffects) {
      try { await this.prepareEffects(); }
      catch (error) {
        if (generation !== this.generation) return;
        this.resumeAfterEffects = false;
        throw error;
      }
    }
    if (playing && this.usingEffects && typeof this.context.startRendering !== 'function')
      await new Promise(resolve => setTimeout(resolve, 80));
    if (generation !== this.generation) return;
    this.resumeAfterEffects = false;
    if (playing) await this.play();
  }

  async loadEncodedBuffer(encoded, index) {
    const generation = this.loadGeneration;
    // decodeAudioData detaches its input; copy so IndexedDB/cache callers retain ownership.
    const data = encoded instanceof ArrayBuffer ? encoded.slice(0)
      : ArrayBuffer.isView(encoded) ? encoded.buffer.slice(encoded.byteOffset, encoded.byteOffset + encoded.byteLength)
      : null;
    if (!data) throw new Error('Missing encoded audio data');
    const buffer = await this.context.decodeAudioData(data);
    if (generation !== this.loadGeneration) throw new Error('Audio loading was cancelled');
    this.setBuffer(buffer, index);
  }

  setBuffer(buffer, index) {
    if (!this.channels[index]) throw new Error(`Unknown channel ${index}`);
    const channel = this.channels[index];
    if (channel.effectNode) {
      channel.effectNode.stop();
      if (this.effectDriver) this.effectDriver.disconnect(channel.effectNode);
      channel.effectNode.disconnect();
      channel.effectNode.port.postMessage({ type: 'dispose' });
      channel.effectNode.port.close();
      channel.effectNode = null;
    }
    this.channels[index].buffer = buffer;
    this.channels[index].padded = null;
    this.duration = Math.max(0, ...this.channels.map(channel => channel.buffer?.duration || 0));
  }

  loadBuffers(buffers) { buffers.forEach((buffer, index) => this.setBuffer(buffer, index)); }

  updateMemoryUsage() {
    const bytes = this.channels.reduce((sum, channel) => sum +
      (channel.buffer ? channel.buffer.length * channel.buffer.numberOfChannels * 4 : 0), 0);
    window.ramUsage = `${(bytes / 1048576).toFixed(1)} mb`;
  }

  ramp(param, value) {
    const now = this.context.currentTime;
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
    param.linearRampToValueAtTime(value, now + 0.005);
  }

  setChannel(index, name, value) {
    const channel = this.channels[index];
    if (!channel) return;
    if (name === 'channelGain' && Number.isFinite(Number(value))) channel.level = Math.max(0, Number(value));
    if (name === 'mute') channel.muted = !!value;
    if (name === 'arm') channel.armed = !!value;
    if (name === 'pan' && Number.isFinite(Number(value))) channel.balance = Math.max(0, Math.min(1, Number(value)));
    this.ramp(channel.gain.gain, channel.muted ? 0 : channel.level);
    this.ramp(channel.left.gain, Math.min(1, 2 * (1 - channel.balance)));
    this.ramp(channel.right.gain, Math.min(1, 2 * channel.balance));
  }

  peak(analyser) {
    analyser.getFloatTimeDomainData(analyser.samples);
    return analyser.samples.reduce((maximum, sample) => Math.max(maximum, Math.abs(sample)), 0);
  }

  sendState() {
    const currentPosition = this.position * 1000;
    if (this.playing && !this.loop && currentPosition >= this.duration * 1000) this.pause();
    this.emit({ event: 'data-transfer-current-engine-state', value: {
      currentPosition, peaks: { channels: this.channels.map(channel => this.peak(channel.analyser)),
        master: this.peak(this.masterAnalyser) }
    } });
  }

  async prepareRecorder() {
    if (this.recorder) return;
    await this.context.audioWorklet.addModule(`${process.env.PUBLIC_URL || ''}/processorScripts/pcm-recorder.js`);
    this.recorder = new AudioWorkletNode(this.context, 'pcm-recorder', { outputChannelCount: [2] });
    this.input.connect(this.recorder);
    this.recorder.connect(this.context.destination); // Worklet outputs silence: no microphone feedback.
    this.recorder.port.onmessage = ({ data }) => {
      if (data.type !== 'recorded') return;
      const targets = this.recordingTargets || [];
      this.recordingTargets = null;
      for (const target of targets) {
        const index = this.channels.indexOf(target);
        if (index < 0) continue;
        this.emit({ event: 'bufferRecorded', data: { buffer: data.buffer.slice(0),
          index, id: target.id, sampleRate: this.context.sampleRate } });
      }
    };
  }

  async record() {
    if (!this.recorder) throw new Error('Microphone is not ready');
    // Record into the original song timeline; transformed recording needs a separate take workflow.
    await this.setEffects('playbackRate', 1);
    await this.setEffects('pitchShiftCents', 0);
    this.pause();
    this.loop = null;
    this.recordingTargets = this.channels.filter(channel => channel.armed);
    const when = this.context.currentTime + 0.05;
    this.recorder.port.postMessage({ type: 'record', startFrame: Math.round(when * this.context.sampleRate),
      offset: Math.round(this.offset * this.context.sampleRate),
      frames: Math.ceil(this.duration * this.context.sampleRate) });
    this.schedule(when);
  }

  sendMessageToAudioScope(message) {
    if (message.type === 'parameterChange') {
      const { scope, paramName, channelIndex, value } = message.data;
      if (scope === 'channel') this.setChannel(channelIndex, paramName, value);
      if (scope === 'global') return this.setEffects(paramName, value);
      return;
    }
    const p = message.payload || {};
    switch (p.id) {
      case 'startPlayback': return this.play(typeof this.context.startRendering === 'function');
      case 'pausePlayback': return this.pause();
      case 'stopPlayback': return this.stop();
      case 'seek': return this.seek(p.value)?.catch(error => this.emit({ event: 'error', message: error.message }));
      case 'setLoop': return this.setLoop(p.times.startMs, p.times.endMs)?.catch(error => this.emit({ event: 'error', message: error.message }));
      case 'setMasterMute': return this.ramp(this.master.gain, p.value ? 0 : 1);
      case 'requestCurrentEngineState': return this.sendState();
      case 'addNewChannel': {
        const restart = !!this.effectsSession && (this.playing || this.startPending);
        if (restart) this.pause();
        this.channels.push(this.makeChannel(p.channelId));
        return restart ? this.play().catch(error => this.emit({ event: 'error', message: error.message })) : undefined;
      }
      case 'removeChannel': {
        const restart = !!this.effectsSession && (this.playing || this.startPending);
        if (restart) this.pause();
        const channel = this.channels[p.index];
        if (channel) {
          if (channel.source) { channel.source.stop(); channel.source.disconnect(); }
          this.disconnectChannel(channel);
          this.channels.splice(p.index, 1);
        }
        return restart ? this.play().catch(error => this.emit({ event: 'error', message: error.message })) : undefined;
      }
      case 'startRecording': return this.record();
      case 'stopRecording':
        if (this.recorder) this.recorder.port.postMessage({ type: 'stop' });
        return this.stop();
      case 'setAndRequestPunchInPoint':
      case 'setAndRequestPunchOutPoint':
        this.emit({ event: p.id === 'setAndRequestPunchInPoint' ? 'data-transfer-punch-in-point' : 'data-transfer-punch-out-point', value: this.position * 1000 });
        return;
      default: return;
    }
  }

  disconnect() {
    ++this.loadGeneration;
    this.stop();
    this.channels.forEach(channel => this.disconnectChannel(channel));
    this.channels = [];
    this.master.disconnect();
    this.masterAnalyser.disconnect();
    if (this.effectDriver) { this.effectDriver.stop(); this.effectDriver.disconnect(); this.effectDriver = null; }
    if (this.recorder) {
      this.input.disconnect(this.recorder);
      this.recorder.port.onmessage = null;
      this.recorder.disconnect();
      this.recorder.port.postMessage({ type: 'cancel' });
      this.recorder = null;
    }
    this.recordingTargets = null;
    window.ramUsage = '0 mb';
  }
}
